import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from './SEO';
import { PlusIcon, PencilIcon, TrashIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import DisclaimerFooter from './DisclaimerFooter';

export default function AnalyzePage() {
  const [watchlists, setWatchlists] = useState([]);
  const [isApproved, setIsApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWatchlist, setEditingWatchlist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const navigate = useNavigate();

  const fetchWatchlists = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/watchlists`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Check if response has watchlists array or is structured differently
      if (Array.isArray(response.data)) {
        setWatchlists(response.data);
      } else if (response.data.watchlists) {
        setWatchlists(response.data.watchlists);
      } else {
        setWatchlists(response.data);
      }
    } catch (error) {
      console.error('Error fetching watchlists:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsApproved(response.data.is_approved === true);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setIsApproved(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  const handleCreateWatchlist = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/watchlists`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setWatchlists([response.data, ...watchlists]);
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Error creating watchlist:', error);
      alert(error.response?.data?.error || 'Failed to create watchlist');
    }
  };

  const handleUpdateWatchlist = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/watchlists/${editingWatchlist.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setWatchlists(watchlists.map(w => w.id === editingWatchlist.id ? response.data : w));
      setEditingWatchlist(null);
      setFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Error updating watchlist:', error);
      alert(error.response?.data?.error || 'Failed to update watchlist');
    }
  };

  const handleDeleteWatchlist = async (watchlistId) => {
    if (!window.confirm('Are you sure you want to delete this watchlist? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setWatchlists(watchlists.filter(w => w.id !== watchlistId));
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      alert(error.response?.data?.error || 'Failed to delete watchlist');
    }
  };

  const openEditModal = (watchlist) => {
    setEditingWatchlist(watchlist);
    setFormData({
      name: watchlist.name,
      description: watchlist.description || ''
    });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setEditingWatchlist(null);
    setFormData({ name: '', description: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-100"></div>
      </div>
    );
  }

  // Subscription banner for unapproved users
  const SubscriptionBanner = () => {
    if (isApproved) return null;
    
    return (
      <div className="mt-4 mb-6 bg-gradient-to-r from-primary-100/20 to-accent-100/20 rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0 md:mr-6">
            <h3 className="text-lg font-semibold text-text-100">
              <LockClosedIcon className="h-5 w-5 inline-block mr-2" />
              Unlock Full Access to Custom Watchlists
            </h3>
            <p className="mt-1 text-text-200">
              Get full access to create and manage custom watchlists with our proprietary ranking system and analytics.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center rounded-md bg-primary-100 px-4 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
            >
              Request Full Access
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO title="Analyze" description="Build and analyze watchlists with factor and sector attribution." />
      <div className="flex-grow bg-bg-100 px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-text-100">Analyze</h1>
            <p className="mt-2 text-sm text-text-200">
              Create and manage custom portfolios or watchlists to analyze with our proprietary ranking system.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              disabled={!isApproved}
              className="inline-flex items-center rounded-md bg-primary-100 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              New Watchlist
            </button>
          </div>
        </div>

        <SubscriptionBanner />

        {/* Watchlists Grid */}
        {isApproved && (
        <div className="mt-8">
          {watchlists.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-text-300">
                <ChartBarIcon className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-semibold text-text-100">No watchlists</h3>
              <p className="mt-1 text-sm text-text-200">
                Get started by creating your first watchlist to analyze stocks.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center rounded-md bg-primary-100 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                  New Watchlist
                </button>
              </div>
            </div>
          ) : (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {watchlists.map((watchlist) => (
                    <div
                      key={watchlist.id}
                      className="bg-bg-200 rounded-lg shadow-sm border border-bg-300 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/analyze/${watchlist.id}`)}
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-text-100">{watchlist.name}</h3>
                          </div>
                          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => openEditModal(watchlist)}
                              className="text-text-300 hover:text-text-100"
                              title="Edit watchlist"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteWatchlist(watchlist.id)}
                              className="text-text-300 hover:text-red-400"
                              title="Delete watchlist"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {watchlist.description && (
                          <p className="mt-1 text-sm text-text-200">{watchlist.description}</p>
                        )}
                        <div className="mt-2 flex items-center justify-between text-xs text-text-300">
                          <span>Created {new Date(watchlist.created_at).toLocaleDateString()}</span>
                          <span>Updated {new Date(watchlist.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </div>)}

        {/* Enterprise CTA Section */}
        {!isApproved && (
          <div className="mt-8">
            <div className="rounded-lg bg-gradient-to-r from-primary-100/10 to-accent-100/10 p-8">
              <div className="mx-auto max-w-md text-center">
                <h2 className="text-xl font-semibold text-text-100">
                  Want Access to Custom Watchlist Analysis?
                </h2>
                <p className="mt-2 text-text-200">
                  Get full access to create and manage custom watchlists, track specific stocks, 
                  and analyze performance with our proprietary ranking system across over 25,000 securities.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                  >
                    Contact Us for Enterprise Access
                    <span className="ml-2" aria-hidden="true">&rarr;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || editingWatchlist) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-bg-200 rounded-lg p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold text-text-100 mb-4">
                {editingWatchlist ? 'Edit Watchlist' : 'Create New Watchlist'}
              </h2>
              <form onSubmit={editingWatchlist ? handleUpdateWatchlist : handleCreateWatchlist}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-100 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-bg-300 text-text-100 rounded-lg border border-bg-400 px-3 py-2 focus:ring-primary-100 focus:border-primary-100"
                      placeholder="Enter watchlist name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-100 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full bg-bg-300 text-text-100 rounded-lg border border-bg-400 px-3 py-2 focus:ring-primary-100 focus:border-primary-100"
                      placeholder="Enter description (optional)"
                      rows={3}
                    />
                  </div>

                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-4 py-2 text-sm font-medium text-text-200 hover:text-text-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium bg-primary-100 text-text-100 rounded-md hover:bg-primary-200"
                  >
                    {editingWatchlist ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
} 